/* eslint-disable */
import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Eye, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import { urls } from '@/Constants';
import { purchaseRequestServices } from '@/Services/PurchaseRequestServices/PurchaseRequestService';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';
import { useSelector, useDispatch } from 'react-redux';
import userRolesAndPermissions from '@/Services/userRolesAndPermissions.services';
import { useAuth } from '@/Components/Auth/Login/auth';
import { modalActions } from '@/Actions';
import PRApprovalModalContent from './PRApprovalModalContent';
import { CustomModal } from '@/Components/Modals/CustomModal';

function PurchaseRequestList() {
    const history = useHistory();
    const dispatch = useDispatch();
    const { userConfig = {} } = useAuth();
    const { level } = userConfig?.userDetails || {};
    const { userRolesDict = {} } = userConfig || {};
    const storeList = useSelector((state) => state.form?.FILTERED_STORES?.data?.LIST || []);

    const { data: prResponse, isLoading: isLoadingPRs, refetch } = useQuery({
        queryKey: ['purchaseRequests'],
        queryFn: () => purchaseRequestServices.fetchPurchaseRequestList({ data: {} }),
        staleTime: Infinity,
    });

    const { data: usersResponse, isLoading: isLoadingUsers } = useQuery({
        queryKey: ['usersList'],
        queryFn: () => userRolesAndPermissions.getUsersList(),
        staleTime: Infinity,
    });

    const prList = prResponse?.data || [];
    const usersList = usersResponse?.data || [];

    const isLoading = isLoadingPRs || isLoadingUsers;

    const columns = [
        {
            title: 'ID',
            key: 'id',
            width: 60,
            customCol: true,
        },
        {
            title: 'PR NAME',
            key: 'prName',
            width: 250,
            customCol: true,
        },
        {
            title: 'REQUESTED BY',
            key: 'requestedBy',
            width: 200,
            customCol: true,
        },
        {
            title: 'STORE NAME',
            key: 'storeName',
            width: 200,
            customCol: true,
        },
        {
            title: 'STATUS',
            key: 'status',
            width: 180,
            customCol: true,
        },
        {
            title: 'CREATED AT',
            key: 'createdAt',
            width: 180,
            customCol: true,
        },
        {
            title: 'APPROVE PR',
            key: 'approve',
            width: 150,
            customCol: true,
        },
    ];

    const customRowData = {
        id: (row) => <span className='text-gray-500 font-mono text-xs ml-3'>{row.id}</span>,
        prName: (row) => <span className='font-medium text-gray-700 ml-3'>{row.description || `PR #${row.id}`}</span>,
        requestedBy: (row) => {
            const user = usersList.find(u => u.id === row.requestedBy);
            return <span className='text-gray-600 ml-3'>{user?.displayName || row.requestedBy}</span>;
        },
        storeName: (row) => {
            const store = storeList.find(s => s.id === row.storeId);
            return <span className='text-gray-600 ml-3'>{store?.name || row.storeId}</span>;
        },
        status: (row) => {
            const isApproved = row.approvalStatus?.isFullyApproved;
            const isRejected = row.approvalStatus?.isPermanentlyRejected;

            let status = 'Pending Approval';
            let colorClass = 'bg-blue-50 text-blue-700 border-blue-200';

            if (isApproved) {
                status = 'Approved';
                colorClass = 'bg-green-50 text-green-700 border-green-200';
            } else if (isRejected) {
                status = 'Rejected';
                colorClass = 'bg-red-50 text-red-700 border-red-200';
            }

            return (
                <div className={`px-3 py-1 rounded-full border text-[11px] font-bold inline-block ml-3 ${colorClass}`}>
                    {status}
                </div>
            );
        },
        createdAt: (row) => {
            if (!row.createdAt) return '-';
            const date = new Date(row.createdAt);
            return (
                <div className='flex flex-col text-[11px] text-gray-500 ml-3'>
                    <span className='font-bold text-gray-700'>{date.toLocaleDateString()}</span>
                    <span>{date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
            );
        },
        approve: (row) => {
            const pendingLevel = row.approvalStatus?.currentPendingUserLevel;
            const pendingRoles = row.approvalStatus?.currentPendingRoles || [];
            const userRoles = Object.values(userRolesDict);
            const isLevelMatch = pendingLevel ? String(level) === String(pendingLevel) : true;
            const isRoleMatch = pendingRoles.some(role => userRoles.includes(role));
            const isFinished = row.approvalStatus?.isFullyApproved || row.approvalStatus?.isPermanentlyRejected;

            if (isFinished) return null;

            if (isLevelMatch && isRoleMatch) {
                return (
                    <Button
                        size='sm'
                        className='bg-green-600 hover:bg-green-700 text-white h-8 text-[11px] font-bold px-3 ml-3 tracking-wide shadow-sm transition-all hover:scale-105 active:scale-95'
                        onClick={(e) => {
                            e.stopPropagation();
                            dispatch(modalActions.toggleCustomModal(true, {
                                width: '1000px',
                                height: 'auto',
                                bodyContent: <PRApprovalModalContent rowData={row} refetch={refetch} />,
                            }));
                        }}
                    >
                        {pendingRoles.length > 0 ? `${pendingRoles.join(', ')}` : `Level ${pendingLevel}`} APPROVE
                    </Button>
                );
            }

            return (
                <div className='flex flex-col ml-3'>
                    <span className='text-[10px] text-gray-400 uppercase font-bold tracking-wider'>Pending Approval</span>
                    <span className='text-[11px] text-blue-600 font-semibold'>
                        {pendingRoles.length > 0 ? `${pendingRoles.join(', ')}` : `Level ${pendingLevel}`}
                    </span>
                </div>
            );
        },
    };

    const handleCustomCol = useMemo(() => (column) => {
        return (
            <p className='text-[14px] w-full uppercase tracking-tight'>
                {column.title}
            </p>
        );
    }, []);

    return (
        <div className='bg-white rounded-lg shadow-sm p-4'>
            <CustomModal />
            <div className='flex justify-between items-center mb-6'>
                <h1 className='text-xl font-bold text-gray-800 tracking-tight'>Purchase Requests</h1>
                <Button
                    className='bg-yellow-400 hover:bg-yellow-500 text-black px-4 h-9 gap-2 flex items-center text-sm shadow-sm font-bold transition-colors'
                    onClick={() => history.push(`${urls.EXTENSION}${urls.PURCHASE_REQUEST_CREATE}`)}
                >
                    <Plus size={16} />
                    <span>Raise PR</span>
                </Button>
            </div>

            <div className='border border-gray-200 rounded-md overflow-auto h-[calc(100vh-200px)]'>
                <VariantTable
                    data={prList}
                    columns={columns}
                    customRowData={customRowData}
                    customCol={handleCustomCol}
                    isLoading={isLoading}
                    customInfoLine='No purchase requests found.'
                />
            </div>
        </div>
    );
}

export default PurchaseRequestList;